/**
 * Zentraler Laufzeit-State + Persistenz.
 *
 * Der `state` enthält sowohl flüchtige Laufzeitwerte (Audio-Knoten, RPM …)
 * als auch persistente Konfiguration (mix, settings, rpmBands, Profile).
 * Nur die persistenten Teile werden in localStorage gespeichert.
 */
import { FACTORY_PROFILES, PRESETS } from '../data/profiles.js'
import { log } from '../util.js'

const STORAGE_KEY = 'revx.config.v1'

/** Default-Mix (entspricht dem ausgelieferten "Tesla/Beast"-Hybrid des Originals). */
const DEFAULT_MIX = {
  master: 1.18,
  deep: 0.42,
  punch: 1.14,
  body: 1.2,
  bark: 0.9,
  edge: 0.46,
  events: 0.82,
  subGuard: 0.72,
  airfix: 0.74,
  normalizer: 1.52,
}
const DEFAULT_SETTINGS = {
  loadLoss: true,
  launch: true,
  extended: true,
  airfix: true,
  mirror: false,
  debugTelemetry: false,
}
const DEFAULT_BANDS = [0.72, 0.9, 1.06, 1.16, 1.18, 1.15, 1.1, 1.02]

/** Vollständiger Laufzeit-State. Audio-Knoten werden erst zur Laufzeit gefüllt. */
export const state = {
  // --- Audio-Graph (flüchtig) ---
  ctx: null,
  master: null,
  comp: null,
  limiter: null,
  analyser: null,
  dest: null,
  spatial: null,
  eventBus: null,
  audioMode: 'worklet',
  carrier: null,
  buffers: {},
  sources: [],
  strips: [],
  // --- Laufzeit / Physik (flüchtig) ---
  engine: false,
  started: false,
  rpm: 0,
  speed: 0,
  gear: 0,
  throttle: 0,
  targetThrottle: 0,
  load: 0,
  peak: 0,
  demo: false,
  sensor: false,
  launch: false,
  route: false,
  lastT: performance.now(),
  shift: 0,
  shiftDir: 0,
  shiftCool: 0,
  downCool: 0,
  overrun: 0,
  engineBrake: 0,
  manualHold: false,
  sensorBias: 0,
  sensorTarget: 0,
  sensorAccel: 0,
  sensorBrake: 0,
  sensorJerk: 0,
  sensorConfidence: 0,
  motionPermission: 'unknown',
  gpsPermission: 'unknown',
  sensorPermissionIssue: '',
  lateralG: 0,
  gPunch: 0,
  lastThrottle: 0,
  rpmDrop: 0,
  pendingShiftRpm: 0,
  shiftBlend: 0,
  stable: 0,
  lastLoss: 0,
  gSensor: 0,
  soundPreviewActive: false,
  // Sensor-Fusion: echte Beschleunigung (m/s²) + GPS-Geschwindigkeit (km/h)
  longAccelMs: 0,
  latAccelMs: 0,
  longBias: 0,
  latBias: 0,
  longRaw: 0,
  latRaw: 0,
  sensorAxis: 'x',
  sensorCalibration: null,
  gpsSpeed: 0,
  gpsActive: false,
  gpsLastPosition: null,
  gpsAccuracy: 0,
  gpsSpeedSource: 'none',
  gpsAgeSec: 0,
  gpsLeadKmh: 0,
  bandBars: new Array(32).fill(0),
  // --- Persistent ---
  profile: FACTORY_PROFILES[0],
  activeProfileId: FACTORY_PROFILES[0].id,
  customProfiles: [],
  settings: { ...DEFAULT_SETTINGS },
  mix: { ...DEFAULT_MIX },
  activeMixPreset: 'custom',
  rpmBands: [...DEFAULT_BANDS],
}

/** Liefert alle Profile (Werk + eigene). */
export function allProfiles() {
  return [...FACTORY_PROFILES, ...state.customProfiles]
}

/** Effektive Drehzahlgrenze des aktiven Profils (Setting "Erweiterte Drehzahl" begrenzt). */
export function redlineRpm() {
  const r = state.profile?.redline || 7000
  return state.settings.extended ? r : Math.min(r, 6400)
}

/** Leerlaufdrehzahl des aktiven Profils. */
export function idleRpm() {
  return state.profile?.idle || 820
}

/** Persistente Teilmenge des States. */
function snapshot() {
  return {
    activeProfileId: state.activeProfileId,
    customProfiles: state.customProfiles,
    settings: state.settings,
    mix: state.mix,
    activeMixPreset: state.activeMixPreset,
    rpmBands: state.rpmBands,
  }
}

/** Speichert die Konfiguration in localStorage (fehlertolerant). */
export function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot()))
  } catch (e) {
    log.warn('Persistenz fehlgeschlagen:', e.message)
  }
}

/** Lädt die Konfiguration aus localStorage und mergt sie defensiv. */
export function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const data = JSON.parse(raw)
    if (data.settings) state.settings = { ...DEFAULT_SETTINGS, ...data.settings }
    if (data.mix) state.mix = { ...DEFAULT_MIX, ...data.mix }
    if (data.activeMixPreset) state.activeMixPreset = data.activeMixPreset
    if (Array.isArray(data.rpmBands) && data.rpmBands.length === 8) state.rpmBands = data.rpmBands
    if (Array.isArray(data.customProfiles))
      state.customProfiles = data.customProfiles.filter((p) => p && p.id)
    if (data.activeProfileId) state.activeProfileId = data.activeProfileId
    const p = allProfiles().find((x) => x.id === state.activeProfileId)
    if (p) state.profile = p
  } catch (e) {
    log.warn('Konfiguration konnte nicht geladen werden:', e.message)
  }
}

export function applyMixPreset(id) {
  const preset = PRESETS[id]
  if (!preset) throw new Error(`Unknown mix preset: ${id}`)
  state.mix = { ...state.mix, ...preset }
  state.activeMixPreset = id
  persist()
}

/** Fügt ein eigenes Profil hinzu (oder ersetzt es bei gleicher ID) und speichert. */
export function upsertCustomProfile(profile) {
  const i = state.customProfiles.findIndex((p) => p.id === profile.id)
  if (i >= 0) state.customProfiles[i] = profile
  else state.customProfiles.push(profile)
  persist()
}

/** Entfernt ein eigenes Profil. */
export function removeCustomProfile(id) {
  state.customProfiles = state.customProfiles.filter((p) => p.id !== id)
  persist()
}
